import { Alert, useMediaQuery } from '@mui/material';
import type {
  DataGridProps,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DataGrid, GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import { useCampaignList } from '@features/campaign-list';
import type { FilterParams } from '@features/campaign-list';
import { useState } from 'react';
import type { ExportCampaignRow } from './columns';
import { exportColumns } from './columns';
import { getDateWindow, hasDateOverlap } from '../../domain/date-window';
import type { DateWindow } from '../../domain/date-window';
import type { ExportFilterState } from './filter-state';

const SHORT_SCREEN_PAGE_SIZE = 5;
const DEFAULT_PAGE_SIZE = 10;
const MAX_SELECTED_ROWS = 5;
const PAGE_SIZES = [SHORT_SCREEN_PAGE_SIZE, DEFAULT_PAGE_SIZE, 25];
const ROW_DISABLED_CLASS = 'row-disabled';

const columns: DataGridProps<ExportCampaignRow>['columns'] = [
  {
    field: GRID_CHECKBOX_SELECTION_FIELD,
    renderHeader: () => null,
  },
  ...exportColumns,
];

const toFilterParams = (filterState: ExportFilterState): FilterParams => ({
  searchText: filterState.searchText,
  selectedCampaignType: filterState.selectedCampaignType,
  selectedReportType: 'overall',
  selectedClient: filterState.selectedClient,
  checkedAdvertisers: filterState.selectedAdvertiser
    ? [filterState.selectedAdvertiser]
    : [],
});

const isRowOverlapping = (
  row: ExportCampaignRow,
  dateWindow: DateWindow | null
): boolean =>
  !dateWindow ||
  hasDateOverlap(row.campaignStartDate, row.campaignEndDate, dateWindow);

type ExportCampaignDataGridProps = {
  filterState: ExportFilterState;
  onSelectionChange: (selectedRows: ExportCampaignRow[]) => void;
  selectedRows: ExportCampaignRow[];
};

export const ExportCampaignDataGrid = ({
  filterState,
  onSelectionChange: handleSelectionChange,
  selectedRows,
}: ExportCampaignDataGridProps) => {
  const isShortScreen = useMediaQuery('(max-height: 800px)');
  const initialPageSize = isShortScreen
    ? SHORT_SCREEN_PAGE_SIZE
    : DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const dateWindow: DateWindow | null = getDateWindow(
    selectedRows.map(r => ({
      start: r.campaignStartDate,
      end: r.campaignEndDate,
    }))
  );

  const { isFetching, error, campaignList, total } = useCampaignList(
    toFilterParams(filterState),
    {
      page,
      pageSize,
      sortKey: 'campaignStartDate',
      sortDirection: 'desc',
    }
  );

  if (error) return <Alert severity="error">Error loading campaign list</Alert>;

  const handlePaginationModelChange: DataGridProps['onPaginationModelChange'] =
    model => {
      setPage(model.page);
      setPageSize(model.pageSize);
    };

  const selectionModel: GridRowSelectionModel = selectedRows.map(
    r => r.campaignId
  );

  const handleSelectionModelChange: DataGridProps['onRowSelectionModelChange'] =
    newModel => {
      const newIds = new Set(newModel);
      const retained = selectedRows.filter(r => newIds.has(r.campaignId));
      const retainedIds = new Set(retained.map(r => r.campaignId));
      const added = campaignList.filter(
        r =>
          newIds.has(r.campaignId) &&
          !retainedIds.has(r.campaignId) &&
          isRowEnabled(r)
      );
      handleSelectionChange([...retained, ...added]);
    };

  const isAtMaxSelection = selectionModel.length >= MAX_SELECTED_ROWS;

  const isRowEnabled = (row: ExportCampaignRow): boolean =>
    selectionModel.includes(row.campaignId) ||
    (!isAtMaxSelection && isRowOverlapping(row, dateWindow));

  return (
    <DataGrid
      checkboxSelection
      columns={columns}
      getRowClassName={(params: GridRowParams<ExportCampaignRow>) =>
        isRowEnabled(params.row) ? '' : ROW_DISABLED_CLASS
      }
      getRowId={row => row.campaignId}
      keepNonExistentRowsSelected
      loading={isFetching}
      onPaginationModelChange={handlePaginationModelChange}
      onRowSelectionModelChange={handleSelectionModelChange}
      pageSizeOptions={PAGE_SIZES}
      paginationMode="server"
      paginationModel={{ page, pageSize }}
      rowCount={total ?? 0}
      rowSelectionModel={selectionModel}
      rows={campaignList}
      sx={{
        height: 'auto',
        marginTop: 2,
        '--DataGrid-overlayHeight': '150px',
        [`& .${ROW_DISABLED_CLASS}`]: {
          opacity: 0.5,
          cursor: 'default',
        },
        [`& .${ROW_DISABLED_CLASS} .MuiCheckbox-root`]: {
          pointerEvents: 'none',
        },
      }}
    />
  );
};
